/**
 * helpers.ts - Dialog Helper Functions
 * 
 * CHANGE HISTORY:
 * - 251025_1735 danielbeer: Migrated from Promptr with enhancements
 * 
 * Pre-configured dialog functions for common use cases
 */

import { PromtrManager } from './PromtrManager';
import { ConfirmConfig, AlertConfig, PromptConfig, ToastConfig } from './types';

/**
 * Create a confirmation dialog
 * 
 * @example
 * ```typescript
 * const confirmed = await confirmDialog(promptr, {
 *   title: 'Delete File',
 *   message: 'Are you sure?'
 * });
 * ```
 */
export const confirmDialog = async (
  promptr: PromtrManager,
  config: {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'info' | 'success' | 'warning' | 'error';
    dangerous?: boolean;
  }
): Promise<boolean> => {
  return await promptr.confirm({
    title: config.title || 'Confirm',
    message: config.message,
    confirmText: config.confirmText || 'Confirm',
    cancelText: config.cancelText || 'Cancel',
    type: config.type || 'info',
    dangerous: config.dangerous || false
  });
};

/**
 * Create a delete confirmation dialog
 * 
 * @example
 * ```typescript
 * const deleted = await deleteDialog(promptr, {
 *   itemName: 'document.txt',
 *   hasChildren: true
 * });
 * ```
 */
export const deleteDialog = async (
  promptr: PromtrManager,
  config: {
    itemName?: string;
    itemCount?: number;
    hasChildren?: boolean;
  }
): Promise<boolean> => {
  const { itemName = 'item', itemCount = 1, hasChildren = false } = config;
  
  const title = itemCount > 1 
    ? `Delete ${itemCount} Items?` 
    : `Delete ${itemName}?`;
    
  const message = hasChildren
    ? 'This will also delete all child items. This action cannot be undone.'
    : itemCount > 1
      ? 'Are you sure you want to delete these items? This action cannot be undone.'
      : 'Are you sure you want to delete this item? This action cannot be undone.';

  return await promptr.confirm({
    title,
    message,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    type: 'error',
    dangerous: true
  });
};

/**
 * Create an alert dialog
 * 
 * @example
 * ```typescript
 * await alertDialog(promptr, {
 *   title: 'Success',
 *   message: 'File saved successfully!',
 *   type: 'success'
 * });
 * ```
 */
export const alertDialog = async (
  promptr: PromtrManager,
  config: {
    title?: string;
    message: string;
    type?: 'info' | 'success' | 'warning' | 'error';
    okText?: string;
  }
): Promise<void> => {
  await promptr.alert({
    title: config.title || 'Alert',
    message: config.message,
    type: config.type || 'info',
    okText: config.okText || 'OK'
  });
};

/**
 * Create an error dialog
 * 
 * @example
 * ```typescript
 * await errorDialog(promptr, {
 *   message: 'Failed to save file',
 *   error: new Error('Network error')
 * });
 * ```
 */
export const errorDialog = async (
  promptr: PromtrManager,
  config: {
    title?: string;
    message: string;
    error?: Error;
    okText?: string;
  }
): Promise<void> => {
  const fullMessage = config.error
    ? `${config.message}\n\nError: ${config.error.message}`
    : config.message;

  await promptr.alert({
    title: config.title || 'Error',
    message: fullMessage,
    type: 'error',
    okText: config.okText || 'OK'
  });
};

/**
 * Create a choice dialog (select from options)
 * 
 * @example
 * ```typescript
 * const choice = await choiceDialog(promptr, {
 *   title: 'Choose Framework',
 *   message: 'Which framework would you like to use?',
 *   choices: ['React', 'Vue', 'Angular']
 * });
 * ```
 */
export const choiceDialog = async <T = string>(
  promptr: PromtrManager,
  config: {
    title: string;
    message?: string;
    choices: Array<{ label: string; value: T } | string>;
    cancelable?: boolean;
  }
): Promise<T | null> => {
  // Convert string choices to objects
  const options = config.choices.map(choice => 
    typeof choice === 'string' 
      ? { label: choice, value: choice as T }
      : choice
  );

  // Use prompt with validation to simulate choice
  // TODO: Replace with proper select dialog when implemented
  const choiceLabels = options.map(o => o.label).join(', ');
  const result = await promptr.prompt({
    title: config.title,
    message: config.message 
      ? `${config.message}\n\nOptions: ${choiceLabels}`
      : `Options: ${choiceLabels}`,
    placeholder: 'Enter your choice...',
    validation: (value) => {
      const found = options.find(o => 
        o.label.toLowerCase() === value.toLowerCase()
      );
      return found ? true : `Please enter one of: ${choiceLabels}`;
    }
  });

  if (!result) return null;

  const selected = options.find(o => 
    o.label.toLowerCase() === result.toLowerCase()
  );
  
  return selected ? selected.value : null;
};

/**
 * Create a form dialog (multiple inputs)
 * 
 * @example
 * ```typescript
 * const data = await formDialog(promptr, {
 *   title: 'User Info',
 *   fields: [
 *     { name: 'username', label: 'Username', type: 'text' },
 *     { name: 'email', label: 'Email', type: 'email' }
 *   ]
 * });
 * ```
 */
export const formDialog = async (
  promptr: PromtrManager,
  config: {
    title: string;
    message?: string;
    fields: Array<{
      name: string;
      label: string;
      type?: 'text' | 'email' | 'password' | 'number';
      defaultValue?: string;
      placeholder?: string;
      required?: boolean;
    }>;
  }
): Promise<Record<string, string> | null> => {
  const result: Record<string, string> = {};

  // Prompt for each field sequentially
  // TODO: Replace with proper form dialog when implemented
  for (const field of config.fields) {
    const value = await promptr.prompt({
      title: `${config.title} - ${field.label}`,
      message: config.message || '',
      defaultValue: field.defaultValue,
      placeholder: field.placeholder,
      type: field.type || 'text',
      validation: field.required 
        ? (v) => v.length > 0 || `${field.label} is required`
        : undefined
    });

    if (value === null) {
      return null; // User canceled
    }

    result[field.name] = value;
  }

  return result;
};

/**
 * Create a copy/paste dialog
 * 
 * @example
 * ```typescript
 * const text = await copyPasteDialog(promptr, {
 *   title: 'Copy Text',
 *   content: 'This is the text to copy'
 * });
 * ```
 */
export const copyPasteDialog = async (
  promptr: PromtrManager,
  config: {
    title?: string;
    message?: string;
    content: string;
    copyButton?: string;
  }
): Promise<boolean> => {
  // Show alert with content
  await promptr.alert({
    title: config.title || 'Copy Text',
    message: config.message 
      ? `${config.message}\n\n${config.content}`
      : config.content,
    type: 'info',
    okText: config.copyButton || 'Copy'
  });

  // Copy to clipboard
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(config.content);
      promptr.success('Copied to clipboard!');
      return true;
    } catch (err) {
      promptr.error('Failed to copy to clipboard');
      return false;
    }
  }

  return false;
};

/**
 * Show a success toast
 * 
 * @example
 * ```typescript
 * successToast(promptr, 'File saved successfully!');
 * ```
 */
export const successToast = (
  promptr: PromtrManager,
  message: string,
  duration?: number
): string => {
  return promptr.success(message, duration);
};

/**
 * Show an error toast
 * 
 * @example
 * ```typescript
 * errorToast(promptr, 'Failed to save file');
 * ```
 */
export const errorToast = (
  promptr: PromtrManager,
  message: string,
  duration?: number
): string => {
  return promptr.error(message, duration);
};

/**
 * Show a warning toast
 * 
 * @example
 * ```typescript
 * warningToast(promptr, 'Please review your changes');
 * ```
 */
export const warningToast = (
  promptr: PromtrManager,
  message: string,
  duration?: number
): string => {
  return promptr.warning(message, duration);
};

/**
 * Show an info toast
 * 
 * @example
 * ```typescript
 * infoToast(promptr, 'New feature available');
 * ```
 */
export const infoToast = (
  promptr: PromtrManager,
  message: string,
  duration?: number
): string => {
  return promptr.info(message, duration);
};
